import React, { type JSX, useRef, useState, useEffect } from 'react';
import { SlideIn, SlideInProps } from '../../../ui/slidein/SlideIn';
import { createPortal } from 'react-dom';

const SLIDEIN_ANIMATION_TIME = 300;

export enum SlideInPortalId {
    USER_ACCOUNT = 'account-level2-slidein',
    PARK_CREATE = 'ParkCreateDropins',
    TRANSACTION_LIST = 'transaction-list-slidein',
    UNAUTH_LANDING_PAGE = 'unauth-landing-page',
    FULL_SCREEN_MODAL = 'fullScreenModal',
}

export interface PortalSlideInProps extends SlideInProps {
    portal: SlideInPortalId;
}

type Props<P> = P & PortalSlideInProps;

export function portalSlideIn<P extends object = object>(
    Comp: React.ComponentType<Props<P>>,
): React.ComponentType<Props<P>> {
    function PortalSlideIn(props: Props<P>): JSX.Element | null {
        // The class mirrored its props into state so the closing frame could
        // keep rendering the last open props. This ref does the same job: it is
        // only ever written while the slide-in is open.
        const lastOpenProps = useRef(props);
        if (props.open) {
            lastOpenProps.current = props;
        }

        const [shouldRender, setShouldRender] = useState(props.open);
        if (props.open && !shouldRender) {
            setShouldRender(true);
        }

        // The class guarded the close transition with the extra `state.open`
        // check so a second prop change could not restart it. This effect only
        // re-runs when `open` or `shouldRender` change, so an unrelated
        // re-render cannot restart it either — and its cleanup clears the
        // timeout on unmount, which the class never did.
        useEffect(() => {
            if (props.open || !shouldRender) {
                return;
            }
            const timeoutId = setTimeout(() => {
                setShouldRender(false);
            }, SLIDEIN_ANIMATION_TIME);
            return () => clearTimeout(timeoutId);
        }, [props.open, shouldRender]);

        const htmlElement = document.getElementById(
            props.portal,
        ) as HTMLElement;
        if (htmlElement === null || !shouldRender) {
            return null;
        }

        const rendered: Props<P> = props.open
            ? props
            : { ...lastOpenProps.current, open: false };

        return createPortal(
            <SelfOpeningSlideIn
                {...rendered}
                contentCssClass={props.contentCssClass}
                level={props.level}
            >
                <Comp {...rendered} />
            </SelfOpeningSlideIn>,
            htmlElement,
        );
    }
    PortalSlideIn.displayName = 'portalSlideIn';
    const component: React.ComponentType<Props<P>> = PortalSlideIn;
    return component;
}

function SelfOpeningSlideIn(props: SlideInProps & { level?: number }) {
    const [open, setOpen] = useState(false);

    // Closing has to take effect in the render pass the prop changes in, as the
    // old UNSAFE_componentWillReceiveProps did, so the outgoing transition
    // starts at the same moment as the parent's unmount timer.
    const [previousOpen, setPreviousOpen] = useState(props.open);
    if (previousOpen !== props.open) {
        setPreviousOpen(props.open);
        if (!props.open) {
            setOpen(false);
        }
    }

    // Opening is deferred by a tick, as the old componentDidMount did, so the
    // browser commits the closed state first and the CSS transition actually
    // runs instead of the panel appearing in place.
    useEffect(() => {
        if (!props.open) {
            return;
        }
        const timeoutId = setTimeout(() => setOpen(true), 10);
        return () => clearTimeout(timeoutId);
    }, [props.open]);

    return <SlideIn {...props} open={open} />;
}

const UserAccountSlideInWrapper = portalSlideIn(p => <>{p.children}</>);

export const UserAccountSlideIn = (p: SlideInProps) => (
    <UserAccountSlideInWrapper {...p} portal={SlideInPortalId.USER_ACCOUNT} />
);
